import React, { useEffect, useState, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Grid,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Autocomplete,
  Divider,
  Link,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Build as MachineIcon,
  LocalShipping as SupplierIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Info as InfoIcon,
  SwapHoriz as SwapHorizIcon,
  CameraAlt as SwitchCameraIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Html5Qrcode, CameraDevice } from 'html5-qrcode';
import { dashboardService, qrcodeService, transactionService, storeService, sparePartService } from '../services';
import { format } from 'date-fns';

interface DashboardStats {
  summary: {
    total_parts: number;
    total_machines: number;
    total_suppliers: number;
    low_stock_count: number;
    total_inventory_value: number;
  };
  recentTransactions: any[];
  lowStockParts: any[];
  stockByCategory: any[];
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mode, setMode] = useState<'details' | 'transaction'>('transaction');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scannedPart, setScannedPart] = useState<any>(null);
  const [transactionType, setTransactionType] = useState<'in' | 'out'>('in');
  const [storeId, setStoreId] = useState<number>(1); // Default to PDS
  const [stores, setStores] = useState<any[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [availableCameras, setAvailableCameras] = useState<CameraDevice[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  
  // Search mode states
  const [searchMode, setSearchMode] = useState(false);
  const [allParts, setAllParts] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    loadDashboardStats();
    loadStores();
    loadCameras();
  }, []);

  const loadCameras = async () => {
    try {
      const devices = await Html5Qrcode.getCameras();
      if (devices && devices.length > 0) {
        console.log('Available cameras:', devices.map(d => ({ id: d.id, label: d.label })));
        
        // Sort cameras: prefer back cameras without "wide" or "ultra" in the name
        const sortedCameras = [...devices].sort((a, b) => {
          const aLabel = a.label.toLowerCase();
          const bLabel = b.label.toLowerCase();
          
          const aIsBack = aLabel.includes('back') || aLabel.includes('rear') || aLabel.includes('environment');
          const bIsBack = bLabel.includes('back') || bLabel.includes('rear') || bLabel.includes('environment');
          
          const aIsWide = aLabel.includes('wide') || aLabel.includes('ultra') || aLabel.includes('0.5');
          const bIsWide = bLabel.includes('wide') || bLabel.includes('ultra') || bLabel.includes('0.5');
          
          // Back cameras first
          if (aIsBack && !bIsBack) return -1;
          if (!aIsBack && bIsBack) return 1;
          
          // Among back cameras, non-wide first
          if (aIsBack && bIsBack) {
            if (!aIsWide && bIsWide) return -1;
            if (aIsWide && !bIsWide) return 1;
          }
          
          return 0;
        });
        
        setAvailableCameras(sortedCameras);
        console.log('Sorted cameras:', sortedCameras.map(d => ({ id: d.id, label: d.label })));
      }
    } catch (err) {
      console.error('Failed to load cameras:', err);
    }
  };

  const loadStores = async () => {
    try {
      const data = await storeService.getAll();
      setStores(data.stores);
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const loadDashboardStats = async () => {
    try {
      const data = await dashboardService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFabClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (selectedMode: 'details' | 'transaction') => {
    setMode(selectedMode);
    setAnchorEl(null);
    setDialogOpen(true);
    setScannedPart(null);
    setSearchMode(false);
    setTransactionType('in');
    setQuantity(1);
    setNotes('');
    setTimeout(() => startScanner(currentCameraIndex), 100);
  };
  
  const loadAllParts = async () => {
    try {
      setSearchLoading(true);
      const data = await sparePartService.getAll();
      setAllParts(data.parts);
    } catch (error) {
      console.error('Failed to load parts:', error);
      setSnackbar({ open: true, message: 'Failed to load parts', severity: 'error' });
    } finally {
      setSearchLoading(false);
    }
  };
  
  const handleSwitchToSearch = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setScanning(false);
    setSearchMode(true);
    loadAllParts();
  };
  
  const handleBackToScanner = () => {
    setSearchMode(false);
    setTimeout(() => startScanner(currentCameraIndex), 100);
  };
  
  const handleSelectPartFromSearch = (part: any) => {
    if (part) {
      setScannedPart(part);
      setSearchMode(false);
    }
  };

  const startScanner = async (cameraIndex: number = currentCameraIndex) => {
    try {
      setScanning(true);
      const html5QrCode = new Html5Qrcode('qr-reader-quick');
      html5QrCodeRef.current = html5QrCode;

      // Use the selected camera or fall back to facingMode
      const cameraId = availableCameras[cameraIndex]?.id;
      
      console.log('Starting camera:', cameraId, availableCameras[cameraIndex]?.label);

      await html5QrCode.start(
        cameraId || { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        () => {}
      );
      
      setCurrentCameraIndex(cameraIndex);
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setSnackbar({ open: true, message: 'Failed to start camera', severity: 'error' });
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.stop();
      html5QrCodeRef.current = null;
    }
    
    const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
    setTimeout(() => startScanner(nextIndex), 300);
  };

  const onScanSuccess = async (decodedText: string) => {
    if (html5QrCodeRef.current) {
      await html5QrCodeRef.current.stop();
      html5QrCodeRef.current = null;
    }
    setScanning(false);

    try {
      const response = await qrcodeService.scan(decodedText);
      setScannedPart(response.part);
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Part not found', 
        severity: 'error' 
      });
      handleCloseDialog();
    }
  };

  const handleCloseDialog = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setDialogOpen(false);
    setScanning(false);
    setScannedPart(null);
    setSearchMode(false);
  };

  const handleSubmitTransaction = async () => {
    if (!scannedPart || quantity <= 0) {
      setSnackbar({ open: true, message: 'Invalid quantity', severity: 'error' });
      return;
    }

    try {
      await transactionService.create({
        spare_part_id: scannedPart.id,
        store_id: storeId,
        transaction_type: transactionType,
        quantity,
        notes,
        transaction_date: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: `${transactionType.toUpperCase()} transaction completed successfully!`,
        severity: 'success',
      });
      
      handleCloseDialog();
      loadDashboardStats(); // Refresh dashboard
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to complete transaction',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const StatCard = ({ title, value, icon, color }: any) => (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4">{value}</Typography>
          </Box>
          <Box
            sx={{
              bgcolor: color + '.light',
              color: color + '.main',
              p: 2,
              borderRadius: 2,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Parts"
            value={stats?.summary.total_parts || 0}
            icon={<InventoryIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Machines"
            value={stats?.summary.total_machines || 0}
            icon={<MachineIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Total Suppliers"
            value={stats?.summary.total_suppliers || 0}
            icon={<SupplierIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title="Low Stock Alerts"
            value={stats?.summary.low_stock_count || 0}
            icon={<WarningIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Inventory Value
                  </Typography>
                  <Typography variant="h4">
                    ${(stats?.summary.total_inventory_value || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'success.light',
                    color: 'success.main',
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <MoneyIcon />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Low Stock Parts
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Part Name</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">Min</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.lowStockParts.slice(0, 10).map((part: any) => (
                    <TableRow key={part.id}>
                      <TableCell>{part.part_number}</TableCell>
                      <TableCell>{part.part_name}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={part.current_stock}
                          size="small"
                          color={part.current_stock === 0 ? 'error' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">{part.minimum_stock}</TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.lowStockParts || stats.lowStockParts.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="textSecondary">No low stock parts</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Transactions
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Part</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats?.recentTransactions.slice(0, 10).map((transaction: any) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Chip
                          label={transaction.transaction_type.toUpperCase()}
                          size="small"
                          color={transaction.transaction_type === 'in' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>{transaction.part_name}</TableCell>
                      <TableCell align="right">{transaction.quantity}</TableCell>
                      <TableCell>
                        {format(new Date(transaction.transaction_date), 'MMM dd, HH:mm')}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!stats?.recentTransactions || stats.recentTransactions.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="textSecondary">No recent transactions</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="quick transaction"
        onClick={handleFabClick}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
      >
        <QrCodeScannerIcon />
      </Fab>

      {/* FAB Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick('details')}>
          <ListItemIcon>
            <InfoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Get Part Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick('transaction')}>
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Create Transaction</ListItemText>
        </MenuItem>
      </Menu>

      {/* Quick Transaction Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {!scannedPart ? (searchMode ? 'Search Part' : 'Scan QR Code') : (mode === 'details' ? 'Part Details' : 'Quick Transaction')}
        </DialogTitle>
        <DialogContent>
          {!scannedPart ? (
            searchMode ? (
              // Search Mode UI
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Search for a part by name or part number
                </Alert>
                
                <Autocomplete
                  options={allParts}
                  getOptionLabel={(option) => 
                    `${option.part_number} - ${option.part_name} (Stock: ${option.current_stock || 0})`
                  }
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    if (!inputValue) return options;
                    
                    return options.filter(option => {
                      const searchFields = [
                        option.part_number,
                        option.manufacturer_part_number,
                        option.sap_number,
                        option.part_name,
                        option.description
                      ];
                      
                      return searchFields.some(field => 
                        field && field.toLowerCase().includes(inputValue)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Parts"
                      placeholder="Search by part number, model, SAP, name, or description..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1, ml: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box sx={{ width: '100%', py: 0.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {option.part_number}
                        </Typography>
                        {(option.manufacturer_part_number || option.sap_number) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {option.manufacturer_part_number && `MFR: ${option.manufacturer_part_number}`}
                            {option.manufacturer_part_number && option.sap_number && ' | '}
                            {option.sap_number && `SAP: ${option.sap_number}`}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {option.part_name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {option.description}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`Stock: ${option.current_stock || 0}`} 
                            size="small" 
                            color={option.current_stock > option.minimum_stock ? 'success' : 'error'}
                          />
                          {option.location && (
                            <Chip label={option.location} size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                    </li>
                  )}
                  onChange={(event, value) => handleSelectPartFromSearch(value)}
                  loading={searchLoading}
                  noOptionsText="No parts found"
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  sx={{ mb: 2 }}
                />
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleBackToScanner}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <ArrowBackIcon fontSize="small" />
                    Back to QR Scanner
                  </Link>
                </Box>
              </Box>
            ) : (
              // QR Scanner Mode UI
              <Box>
                <div
                  id="qr-reader-quick"
                  style={{
                    width: '100%',
                    border: '2px solid #1976d2',
                    borderRadius: '8px',
                    overflow: 'hidden',
                  }}
                />
                {scanning && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2 }}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        align="center"
                      >
                        Position QR code within the frame
                      </Typography>
                      {availableCameras.length > 1 && (
                        <Tooltip title="Switch Camera">
                          <IconButton onClick={switchCamera} color="primary" size="small">
                            <SwitchCameraIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {availableCameras.length > 0 && (
                      <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ mt: 1 }}>
                        Camera: {availableCameras[currentCameraIndex]?.label || 'Default'} ({currentCameraIndex + 1}/{availableCameras.length})
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                        No QR code available?
                      </Typography>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={handleSwitchToSearch}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 0.5,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        <SearchIcon fontSize="small" />
                        Search Part Instead
                      </Link>
                    </Box>
                  </>
                )}
              </Box>
            )
          ) : mode === 'details' ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Part Details Retrieved Successfully
              </Alert>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        {/* Basic Information */}
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                            Part Number:
                          </Typography>
                          <Typography variant="body1">{scannedPart.part_number}</Typography>
                        </Grid>

                        {scannedPart.sap_number && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              SAP Number:
                            </Typography>
                            <Typography variant="body1">{scannedPart.sap_number}</Typography>
                          </Grid>
                        )}

                        {scannedPart.manufacturer_part_number && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Manufacturer Part Number:
                            </Typography>
                            <Typography variant="body1">{scannedPart.manufacturer_part_number}</Typography>
                          </Grid>
                        )}

                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                            Status:
                          </Typography>
                          <Chip
                            label={scannedPart.status?.toUpperCase()}
                            size="small"
                            color={
                              scannedPart.status === 'active'
                                ? 'success'
                                : scannedPart.status === 'discontinued'
                                ? 'warning'
                                : 'error'
                            }
                          />
                        </Grid>

                        {/* Part Name */}
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                            Part Name:
                          </Typography>
                          <Typography variant="body1">{scannedPart.part_name}</Typography>
                        </Grid>

                        {/* Stock Information */}
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 1 }}>
                            Stock Information:
                          </Typography>
                          <Box display="flex" gap={2} flexWrap="wrap">
                            <Chip label={`Total: ${scannedPart.current_stock || 0}`} color="primary" />
                            <Chip label={`PDS: ${scannedPart.pds_stock || 0}`} color="info" />
                            <Chip label={`EMS: ${scannedPart.ems_stock || 0}`} color="info" />
                            <Chip label={`Min: ${scannedPart.minimum_stock || 0}`} color="warning" />
                          </Box>
                        </Grid>

                        {/* Machines */}
                        {scannedPart.machine_names && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Machines:
                            </Typography>
                            <Typography variant="body1">{scannedPart.machine_names}</Typography>
                          </Grid>
                        )}

                        {/* Category */}
                        {scannedPart.category_name && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Category:
                            </Typography>
                            <Typography variant="body1">{scannedPart.category_name}</Typography>
                          </Grid>
                        )}

                        {/* Supplier */}
                        {scannedPart.supplier_name && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Supplier:
                            </Typography>
                            <Typography variant="body1">{scannedPart.supplier_name}</Typography>
                          </Grid>
                        )}

                        {/* Location */}
                        {scannedPart.location && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Location:
                            </Typography>
                            <Typography variant="body1">{scannedPart.location}</Typography>
                          </Grid>
                        )}

                        {/* Shelf Position */}
                        {scannedPart.shelf_position && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Shelf Position:
                            </Typography>
                            <Typography variant="body1">{scannedPart.shelf_position}</Typography>
                          </Grid>
                        )}

                        {/* Description */}
                        {scannedPart.description && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Description:
                            </Typography>
                            <Typography variant="body1">{scannedPart.description}</Typography>
                          </Grid>
                        )}

                        {/* Part Use */}
                        {scannedPart.part_use && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Part Use:
                            </Typography>
                            <Typography variant="body1">{scannedPart.part_use}</Typography>
                          </Grid>
                        )}

                        {/* Unit Price */}
                        {scannedPart.unit_price && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Unit Price:
                            </Typography>
                            <Typography variant="body1">Rs. {scannedPart.unit_price}</Typography>
                          </Grid>
                        )}

                        {/* Unit of Measure */}
                        {scannedPart.unit_of_measure && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                              Unit of Measure:
                            </Typography>
                            <Typography variant="body1">{scannedPart.unit_of_measure}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Part found! Enter transaction details below.
              </Alert>

              <TextField
                fullWidth
                label="Part Number"
                value={scannedPart.part_number}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Part Name"
                value={scannedPart.part_name}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Current Stock"
                value={scannedPart.current_stock}
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                select
                fullWidth
                label="Store Location"
                value={storeId}
                onChange={(e) => setStoreId(Number(e.target.value))}
                sx={{ mb: 2 }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.store_code} - {store.store_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="Transaction Type"
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value as 'in' | 'out')}
                sx={{ mb: 2 }}
              >
                <MenuItem value="in">IN - Add to Stock</MenuItem>
                <MenuItem value="out">OUT - Remove from Stock</MenuItem>
              </TextField>

              <TextField
                fullWidth
                type="number"
                label="Quantity"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{mode === 'details' && scannedPart ? 'Close' : 'Cancel'}</Button>
          {scannedPart && mode === 'transaction' && (
            <Button
              onClick={handleSubmitTransaction}
              variant="contained"
              color="primary"
            >
              Complete Transaction
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;
